/* eslint-disable sonarjs/cognitive-complexity */
import { CodeNameModel } from 'components/common/types';
import { DirectSalesLineObjective } from 'components/common/types/DirectSalesCampaign.types';
import { Store } from 'components/common/types/Store.types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  FormatCategory,
  LocationCategory,
  ObjectiveFormat,
  ReferenceFormat,
  ReferenceLocation,
  ReferenceOption,
} from 'store/userOptions/reducer.types';
import {
  groupFormatSelectionCodes,
  groupLocationSelectionCodes,
  groupTagSelectionCodes,
  isExcludedEmpty,
  isIncludedEmpty,
} from './utils';

const useObjectivesAllowedSelection = (
  activeObjective: DirectSalesLineObjective,
): {
  allowedLocationOptions: ReferenceLocation;
  allowedFormatOptions: ReferenceFormat;
  allowedTagOptions: Record<string, CodeNameModel[]>;
} => {
  const {
    location: locationOptions,
    format: formatOptions,
    tags: tagOptions,
  } = useSelector((state: Store) => state.userOptions.referenceOptions);

  const selectedLocationsCodes = useSelector((state: Store) =>
    groupLocationSelectionCodes(state.dealManagement.directSales.locations),
  );
  const selectedFormatsCodes = useSelector((state: Store) =>
    groupFormatSelectionCodes(state.dealManagement.directSales.dealLineFormats),
  );
  const selectedTagsCodes = useSelector((state: Store) =>
    groupTagSelectionCodes(state.dealManagement.commonDeal.currentLine.tags),
  );

  const allowedLocationOptions = useMemo(() => {
    if (
      isIncludedEmpty(selectedLocationsCodes) &&
      isExcludedEmpty(selectedLocationsCodes) &&
      !activeObjective.locations.length
    )
      return locationOptions;

    const newLocationOptions: ReferenceLocation = structuredClone(locationOptions);

    Object.entries(locationOptions).forEach(
      ([locationKey, locationValues]: [keyof ReferenceLocation, ReferenceOption[]]) => {
        const isIncludedSelectionEmpty = !selectedLocationsCodes[locationKey]?.included.size;
        const isExcludedSelectionEmpty = !selectedLocationsCodes[locationKey]?.excluded.size;

        if (!isIncludedSelectionEmpty) {
          const newOptions: ReferenceOption[] = [];

          locationValues.forEach((locationValue) => {
            if (selectedLocationsCodes[locationKey]?.included.has(locationValue.code)) {
              newOptions.push(locationValue);
            }
          });

          if (!newOptions.length) return;

          newLocationOptions[locationKey] = newOptions;
        } else if (isIncludedSelectionEmpty && !isExcludedSelectionEmpty) {
          const hasExclusion = locationValues.some((locationValue) =>
            selectedLocationsCodes[locationKey]?.excluded.has(locationValue.code),
          );
          if (!hasExclusion) return;

          newLocationOptions[locationKey] = locationValues.filter(
            (locationValue) => !selectedLocationsCodes[locationKey]?.excluded.has(locationValue.code),
          );
        } else {
          newLocationOptions[locationKey] = [...locationValues];
        }
      },
    );

    if (activeObjective.locations.length) {
      const locationsMap: Map<string, { category: string; name: string }> = new Map();

      Object.entries(newLocationOptions).forEach(([key, value]) => {
        value.forEach((item: ReferenceOption) => {
          locationsMap.set(item.code, { category: key, name: item.name });
        });
      });

      activeObjective.locations.forEach(({ code, name, category }) => {
        if (!locationsMap.has(code) && category) {
          if (!newLocationOptions[LocationCategory[category]]) newLocationOptions[LocationCategory[category]] = [];
          newLocationOptions[LocationCategory[category]]?.push({
            code,
            name,
            assets: 0,
          });
        }
      });
    }

    return newLocationOptions;
  }, [activeObjective.locations, selectedLocationsCodes, locationOptions]);

  const allowedFormatOptions = useMemo(() => {
    if (
      isIncludedEmpty(selectedFormatsCodes) &&
      isExcludedEmpty(selectedFormatsCodes) &&
      !activeObjective.formats.length
    )
      return formatOptions;

    const newFormatOptions: ReferenceFormat = structuredClone(formatOptions);

    Object.entries(formatOptions).forEach(([formatKey, formatValues]: [keyof ReferenceFormat, ReferenceOption[]]) => {
      const isIncludedSelectionEmpty = !selectedFormatsCodes[formatKey]?.included.size;
      const isExcludedSelectionEmpty = !selectedFormatsCodes[formatKey]?.excluded.size;

      if (!isIncludedSelectionEmpty) {
        const newOptions: ObjectiveFormat[] = [];

        formatValues.forEach((formatValue) => {
          if (selectedFormatsCodes[formatKey]?.included.has(formatValue.code)) {
            newOptions.push(formatValue as ObjectiveFormat);
          }
        });

        if (!newOptions.length) return;

        newFormatOptions[formatKey] = newOptions;
      } else if (isIncludedSelectionEmpty && !isExcludedSelectionEmpty) {
        const hasExclusion = formatValues.some((formatValue) =>
          selectedFormatsCodes[formatKey]?.excluded.has(formatValue.code),
        );
        if (!hasExclusion) return;

        newFormatOptions[formatKey] = formatValues.filter(
          (formatValue) => !selectedFormatsCodes[formatKey]?.excluded.has(formatValue.code),
        ) as ObjectiveFormat[];
      } else {
        newFormatOptions[formatKey] = [...formatValues] as ObjectiveFormat[];
      }
    });

    if (activeObjective.formats.length) {
      const formatsMap: Map<string, { category: string; name: string }> = new Map();

      Object.entries(newFormatOptions).forEach(([key, value]) => {
        value.forEach((item: ReferenceOption) => {
          formatsMap.set(item.code, { category: key, name: item.name });
        });
      });

      activeObjective.formats.forEach(({ code, name, category }) => {
        if (!formatsMap.has(code) && category) {
          if (!newFormatOptions[FormatCategory[category]]) newFormatOptions[FormatCategory[category]] = [];
          newFormatOptions[FormatCategory[category]].push({
            code,
            name,
            assets: 0,
            businessArea: '',
            frameSizeFamily: '',
          });
        }
      });
    }

    return newFormatOptions;
  }, [activeObjective.formats, selectedFormatsCodes, formatOptions]);

  const allowedTagOptions = useMemo(() => {
    if (isIncludedEmpty(selectedTagsCodes) && isExcludedEmpty(selectedTagsCodes) && !activeObjective.tags.length)
      return tagOptions;

    const newTagOptions: Record<string, CodeNameModel[]> = structuredClone(tagOptions);

    Object.entries(tagOptions).forEach(([tagKey, tagValues]: [string, CodeNameModel[]]) => {
      const isIncludedSelectionEmpty = !selectedTagsCodes[tagKey]?.included.size;
      const isExcludedSelectionEmpty = !selectedTagsCodes[tagKey]?.excluded.size;

      if (!isIncludedSelectionEmpty) {
        const newOptions: ReferenceOption[] = [];

        tagValues.forEach((tagValue) => {
          if (selectedTagsCodes[tagKey]?.included.has(tagValue.code)) {
            newOptions.push(tagValue as ReferenceOption);
          }
        });

        if (!newOptions.length) return;

        newTagOptions[tagKey] = newOptions;
      } else if (isIncludedSelectionEmpty && !isExcludedSelectionEmpty) {
        const hasExclusion = tagValues.some((tagValue) => selectedTagsCodes[tagKey]?.excluded.has(tagValue.code));
        if (!hasExclusion) return;

        newTagOptions[tagKey] = tagValues.filter((tagValue) => !selectedTagsCodes[tagKey]?.excluded.has(tagValue.code));
      } else {
        newTagOptions[tagKey] = [...tagValues];
      }
    });

    if (activeObjective.tags.length) {
      const tagsMap: Map<string, { category: string; name: string }> = new Map();

      Object.entries(newTagOptions).forEach(([key, value]) => {
        value.forEach((item: ReferenceOption) => {
          tagsMap.set(item.code, { category: key, name: item.name });
        });
      });

      activeObjective.tags.forEach(({ category, code, name }) => {
        if (category && !tagsMap.has(category)) {
          if (!newTagOptions[category]) newTagOptions[category] = [];
          newTagOptions[category].push({ code, name });
        }
      });
    }

    return newTagOptions;
  }, [activeObjective.tags, selectedTagsCodes, tagOptions]);

  return {
    allowedLocationOptions,
    allowedFormatOptions,
    allowedTagOptions,
  };
};

export default useObjectivesAllowedSelection;
/* eslint-enable sonarjs/cognitive-complexity */
